import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, LogOut, ArrowLeft, BarChart3, Globe, Database, Gauge, Users, Layers, Cpu, BookOpen } from 'lucide-react';
import DhiLogo from './DhiLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_LOGOS = {
  chatgpt:    'https://cdn.prod.website-files.com/686e2126992edd7b040ec218/69ccedef9ce81893c1ef2c17_chatgpt-icon.webp',
  gemini:     'https://cdn.simpleicons.org/googlegemini/0A0F2E',
  perplexity: 'https://cdn.simpleicons.org/perplexity/0A0F2E',
  claude:     'https://cdn.simpleicons.org/anthropic/0A0F2E',
};
const PLATFORM_LABELS = { chatgpt: 'ChatGPT', gemini: 'Gemini', perplexity: 'Perplexity', claude: 'Claude' };

// Updated 2026-06-09 to match the redesigned scoring (PR #17-20).
// Legacy keys kept for historical audit insights that pre-date the merge.
const WORKER_LABELS = {
  technical: 'AI Access', schema: 'Schema', content: 'Content',
  authority: 'Authority', page_performance: 'Page Perf',
  site_architecture: 'Site Arch',
  pagespeed: 'PageSpeed (legacy)', onpage: 'On-Page (legacy)',
};

const SCHEMA_LABELS = {
  core_hotel:         'Hotel / LodgingBusiness',
  room_inventory:     'HotelRoom / Accommodation',
  dining_service:     'Restaurant / FoodEstablishment',
  event_schema:       'Event / MeetingVenue',
  faq_conversational: 'FAQPage',
  reviews_ratings:    'Review / AggregateRating',
  breadcrumb_search:  'Breadcrumb / Search',
  local_business:     'LocalBusiness',
  hotel_schema_present: 'Hotel (any flavour) present',
};

const PARAM_LABELS = {
  composite_score:     'Composite',
  brand_recognition:   'Brand Recognition',
  competitive_share:   'Competitive Share',
  content_depth:       'Content Depth',
  technical_readiness: 'Technical Readiness',
  trust_signals:       'Trust Signals',
};

const FINDING_SOURCE_LABELS = {
  schema: 'Schema', content: 'Content',
  technical: 'AI Access', authority: 'Authority', geo: 'Geo',
  page_performance: 'Page Perf', site_architecture: 'Site Arch',
  // Findings produced by the legacy pagespeed/onpage workers still carry
  // these `source` values because the page_performance merge in
  // score_aggregator preserves them (PR #18). Keep the labels.
  pagespeed: 'PageSpeed', onpage: 'On-Page',
};

const scoreColor = (s) => (s < 40 ? '#DC2626' : s < 70 ? '#D97706' : '#22c55e');
const fmtNum = (n) => { try { return Number(n).toLocaleString(); } catch { return n; } };
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };
const pct = (n) => `${Math.round((n || 0) * 100)}%`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}`, 'Content-Type': 'application/json' });

const card = { background: '#fff', borderRadius: 12, border: '1px solid rgba(10,10,26,0.08)', padding: '20px 22px' };
const label = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(10,10,26,0.4)' };
const sectionTitle = { fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 700, color: '#0A0A1A', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 };
const ghostBtn = { padding: '7px 14px', border: '1px solid rgba(10,10,26,0.14)', borderRadius: 8, background: '#fff', color: 'rgba(10,10,26,0.52)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: "'DM Sans', sans-serif" };

const Bar = ({ value, max, color = '#2D4DE0', height = 6 }) => (
  <div style={{ width: '100%', height, background: '#F0F1F7', borderRadius: 3, overflow: 'hidden' }}>
    <div style={{ width: `${Math.max(0, Math.min(100, (value / (max || 1)) * 100))}%`, height: '100%', background: color, borderRadius: 3 }} />
  </div>
);

export default function AdminInsights() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/insights`, { headers: authHeaders() });
      if (r.status === 401) { localStorage.removeItem('adminToken'); navigate('/admin/login'); return; }
      if (!r.ok) throw new Error('Failed to load insights');
      const j = await r.json();
      setData(j.data);
    } catch (e) {
      setError(e.message || 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/admin/login'); return; }
    fetchInsights();
  }, [fetchInsights, navigate]);

  const handleLogout = () => { localStorage.removeItem('adminToken'); localStorage.removeItem('adminEmail'); navigate('/admin/login'); };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#F4F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ color: 'rgba(10,10,26,0.5)' }}>Loading insights…</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#F4F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ color: '#DC2626', fontWeight: 700, marginBottom: 8 }}>{error}</div>
        <button onClick={fetchInsights} style={ghostBtn}>Retry</button>
      </div>
    </div>
  );

  if (!data || data.empty) return (
    <div style={{ minHeight: '100vh', background: '#F4F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ color: 'rgba(10,10,26,0.5)' }}>No completed audits yet — run a few audits and come back.</div>
    </div>
  );

  const {
    overview, score_distribution, worker_averages, platform_deep_dive,
    schema_adoption, schema_types_frequency, performance_cliff,
    top_competitors_global, findings_matrix, segmentation, module_frequency,
    editorial_citations,
  } = data;

  // Derive max values for bar scaling
  const maxScoreBucket = Math.max(...score_distribution.map(b => b.count), 1);
  const maxCompetitor = top_competitors_global[0]?.citations || 1;
  const maxModule = module_frequency[0]?.count || 1;
  const maxSchemaType = schema_types_frequency[0]?.count || 1;
  const citationsTop = editorial_citations?.top_global || [];
  const citationsByPlatform = editorial_citations?.by_platform || {};
  const maxCitation = citationsTop[0]?.count || 1;
  const citationsCoverage = editorial_citations
    ? { with: editorial_citations.audits_with_citations || 0, total: editorial_citations.audits_total || 0 }
    : { with: 0, total: 0 };

  // Derive priority columns for findings matrix
  const PRIORITY_ORDER = ['Critical', 'High', 'Medium', 'Low'];
  const priorityColor = { Critical: '#DC2626', High: '#D97706', Medium: '#2D4DE0', Low: 'rgba(10,10,26,0.4)' };
  const findingSources = Object.keys(findings_matrix).sort();

  return (
    <div style={{ minHeight: '100vh', background: '#F4F5F7', fontFamily: "'DM Sans', sans-serif" }} data-testid="admin-insights">
      {/* Header */}
      <div style={{ background: '#0A0F2E', padding: '16px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <DhiLogo variant="white" height={22} />
          <div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 15, color: '#fff' }}>Portfolio Insights</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
              {overview.total_audits} audits · {fmtDate(overview.earliest)}–{fmtDate(overview.latest)}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/admin')} style={{ ...ghostBtn, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', background: 'transparent' }}><ArrowLeft size={14} /> Dashboard</button>
          <button onClick={fetchInsights} style={{ ...ghostBtn, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', background: 'transparent' }}><RefreshCw size={14} /> Refresh</button>
          <button onClick={handleLogout} style={{ ...ghostBtn, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', background: 'transparent' }}><LogOut size={14} /> Logout</button>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>

        {/* ── SCORE DISTRIBUTION + WORKER AVERAGES (2 cols) ────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {/* Score distribution histogram */}
          <div style={card}>
            <div style={sectionTitle}><BarChart3 size={16} color="#2D4DE0" /> Final Score Distribution</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {score_distribution.map((b, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '60px 1fr 40px', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: 'rgba(10,10,26,0.55)', fontWeight: 600 }}>{b.range}</span>
                  <Bar value={b.count} max={maxScoreBucket} color={scoreColor((i * 20) + 10)} height={12} />
                  <span style={{ fontSize: 12, color: 'rgba(10,10,26,0.7)', fontWeight: 700, textAlign: 'right' }}>{b.count}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Worker averages */}
          <div style={card}>
            <div style={sectionTitle}><Gauge size={16} color="#2D4DE0" /> Average Worker Scores</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {Object.entries(worker_averages).map(([k, v]) => (
                <div key={k}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: 'rgba(10,10,26,0.55)', fontWeight: 600 }}>{WORKER_LABELS[k] || k}</span>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 800, color: scoreColor(v) }}>{v}<span style={{ fontSize: 10, color: 'rgba(10,10,26,0.3)', fontWeight: 400 }}>/100</span></span>
                  </div>
                  <Bar value={v} max={100} color={scoreColor(v)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── PLATFORM DEEP-DIVE ──────────────────────────────────────── */}
        <div style={card}>
          <div style={sectionTitle}><Cpu size={16} color="#2D4DE0" /> Platform Deep-Dive — 5 Parameters × 4 LLMs</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(10,10,26,0.08)' }}>
                  <th style={{ ...label, padding: '10px 12px', textAlign: 'left' }}>Parameter</th>
                  {Object.entries(PLATFORM_LABELS).map(([k, l]) => (
                    <th key={k} style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <img src={PLATFORM_LOGOS[k]} alt={l} width={18} height={18} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#0A0A1A' }}>{l}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(PARAM_LABELS).map(pr => (
                  <tr key={pr} style={{ borderBottom: '1px solid rgba(10,10,26,0.04)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: 'rgba(10,10,26,0.65)' }}>{PARAM_LABELS[pr]}</td>
                    {Object.keys(PLATFORM_LABELS).map(p => {
                      const v = platform_deep_dive?.[p]?.[pr] || 0;
                      const max = pr === 'composite_score' ? 100 : 10;
                      return (
                        <td key={p} style={{ padding: '10px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 700, color: '#0A0A1A', minWidth: 40, textAlign: 'right' }}>{v}<span style={{ fontSize: 10, color: 'rgba(10,10,26,0.3)' }}>/{max}</span></span>
                            <Bar value={v} max={max} color={pr === 'composite_score' ? scoreColor(v) : '#2D4DE0'} />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── SCHEMA ADOPTION ─────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 14 }}>
          <div style={card}>
            <div style={sectionTitle}><Database size={16} color="#2D4DE0" /> Schema Adoption Across Portfolio</div>
            <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)', marginBottom: 12 }}>
              % of hotels scoring above zero on each schema dimension.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(schema_adoption).map(([k, v]) => (
                <div key={k} style={{ display: 'grid', gridTemplateColumns: '220px 1fr 80px', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: 'rgba(10,10,26,0.7)', fontWeight: 600 }}>{SCHEMA_LABELS[k] || k}</span>
                  <Bar value={v.rate * 100} max={100} color={v.rate >= 0.7 ? '#22c55e' : v.rate >= 0.4 ? '#D97706' : '#DC2626'} height={10} />
                  <span style={{ fontSize: 12, color: 'rgba(10,10,26,0.7)', fontWeight: 700, textAlign: 'right' }}>{pct(v.rate)} <span style={{ color: 'rgba(10,10,26,0.4)', fontWeight: 400 }}>({v.adopted}/{v.adopted + v.missing})</span></span>
                </div>
              ))}
            </div>
          </div>
          {/* Top schema types found */}
          <div style={card}>
            <div style={sectionTitle}><Layers size={16} color="#2D4DE0" /> Most Common Schema Types</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {schema_types_frequency.slice(0, 15).map((s, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 40px', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: 'rgba(10,10,26,0.7)', fontWeight: 500 }}>{s.type}</span>
                  <Bar value={s.count} max={maxSchemaType} color="#2D4DE0" height={6} />
                  <span style={{ fontSize: 11, color: 'rgba(10,10,26,0.55)', textAlign: 'right' }}>{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── PERFORMANCE CLIFF ───────────────────────────────────────── */}
        <div style={card}>
          <div style={sectionTitle}><Gauge size={16} color="#2D4DE0" /> Performance Cliff — PageSpeed Percentiles Across Portfolio</div>
          <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)', marginBottom: 14 }}>
            Real-world distribution of Core Web Vitals. p50 = median, p75 = 75th percentile, p90 = 90th percentile (tail).
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(10,10,26,0.08)' }}>
                  <th style={{ ...label, padding: '10px 12px', textAlign: 'left' }}>Metric</th>
                  <th style={{ ...label, padding: '10px 12px', textAlign: 'right' }}>p50 (median)</th>
                  <th style={{ ...label, padding: '10px 12px', textAlign: 'right' }}>p75</th>
                  <th style={{ ...label, padding: '10px 12px', textAlign: 'right' }}>p90 (tail)</th>
                  <th style={{ ...label, padding: '10px 12px', textAlign: 'right' }}>n</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['lcp', 'LCP', 'ms', 2500],
                  ['fcp', 'FCP', 'ms', 1800],
                  ['tbt', 'TBT', 'ms', 200],
                  ['cls', 'CLS', '', 0.1],
                  ['page_weight_kb', 'Page Weight', 'kb', 2048],
                ].map(([k, lbl, unit, target]) => {
                  const v = performance_cliff[k] || {};
                  const fmtV = (n) => (n === null || n === undefined) ? '—' : (unit === 'ms' ? `${Math.round(n).toLocaleString()}${unit}` : unit === 'kb' ? `${Math.round(n).toLocaleString()} kb` : `${Number(n).toFixed(2)}`);
                  return (
                    <tr key={k} style={{ borderBottom: '1px solid rgba(10,10,26,0.04)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0A0A1A' }}>{lbl} <span style={{ fontSize: 10, color: 'rgba(10,10,26,0.4)', fontWeight: 400 }}>(target &lt; {unit === 'ms' ? `${target}ms` : unit === 'kb' ? `${target} kb` : target})</span></td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: v.p50 > target ? '#DC2626' : '#22c55e', fontWeight: 700 }}>{fmtV(v.p50)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: v.p75 > target ? '#DC2626' : '#22c55e', fontWeight: 700 }}>{fmtV(v.p75)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: v.p90 > target ? '#DC2626' : '#22c55e', fontWeight: 700 }}>{fmtV(v.p90)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: 'rgba(10,10,26,0.5)' }}>{v.count || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── TOP COMPETITORS + FINDINGS MATRIX ───────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 14 }}>
          <div style={card}>
            <div style={sectionTitle}><Users size={16} color="#2D4DE0" /> Top Competitors Cited Globally</div>
            <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)', marginBottom: 12 }}>
              Aggregated across every audit — which hotels dominate AI recommendation output in your data.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {top_competitors_global.slice(0, 20).map((c, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 60px', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, color: 'rgba(10,10,26,0.35)', fontWeight: 600 }}>{i + 1}.</span>
                  <div>
                    <div style={{ fontSize: 12, color: '#0A0A1A', fontWeight: 600 }}>{c.name}</div>
                    <Bar value={c.citations} max={maxCompetitor} color="#2D4DE0" height={4} />
                  </div>
                  <span style={{ fontSize: 12, color: 'rgba(10,10,26,0.6)', fontWeight: 700, textAlign: 'right' }}>{c.citations}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Findings matrix */}
          <div style={card}>
            <div style={sectionTitle}><BarChart3 size={16} color="#2D4DE0" /> Findings by Worker × Priority</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(10,10,26,0.08)' }}>
                  <th style={{ ...label, padding: '8px 10px', textAlign: 'left' }}>Worker</th>
                  {PRIORITY_ORDER.map(p => (
                    <th key={p} style={{ ...label, padding: '8px 10px', textAlign: 'right', color: priorityColor[p] }}>{p}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {findingSources.map(src => (
                  <tr key={src} style={{ borderBottom: '1px solid rgba(10,10,26,0.04)' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 600, color: '#0A0A1A' }}>{FINDING_SOURCE_LABELS[src] || src}</td>
                    {PRIORITY_ORDER.map(p => (
                      <td key={p} style={{ padding: '8px 10px', textAlign: 'right', color: 'rgba(10,10,26,0.7)', fontWeight: (findings_matrix[src]?.[p] || 0) > 0 ? 700 : 400 }}>
                        {findings_matrix[src]?.[p] || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── EDITORIAL CITATIONS (Phase 2) ───────────────────────────── */}
        <div style={card}>
          <div style={sectionTitle}>
            <BookOpen size={16} color="#2D4DE0" /> Editorial Sources Cited by AI Platforms
          </div>
          <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)', marginBottom: 14 }}>
            Which publications ChatGPT / Gemini / Perplexity / Claude invoke as authority when answering hotel queries.
            Coverage: <strong>{citationsCoverage.with}</strong> of <strong>{citationsCoverage.total}</strong> audits have citation data
            {citationsCoverage.with < citationsCoverage.total && (
              <span style={{ color: 'rgba(10,10,26,0.4)' }}> — older audits predate the extractor and will backfill as you re-probe.</span>
            )}
          </div>

          {citationsTop.length === 0 ? (
            <div style={{ padding: '24px 12px', textAlign: 'center', color: 'rgba(10,10,26,0.4)', fontSize: 13 }}>
              No citations yet — run a fresh audit to start collecting, or re-probe existing hotels.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 20 }}>
              {/* Global top citations */}
              <div>
                <div style={{ ...label, marginBottom: 10 }}>Top Cited Overall</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {citationsTop.slice(0, 20).map((c, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 50px', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 11, color: 'rgba(10,10,26,0.35)', fontWeight: 600 }}>{i + 1}.</span>
                      <div>
                        <div style={{ fontSize: 12, color: '#0A0A1A', fontWeight: 600 }}>{c.publication}</div>
                        <Bar value={c.count} max={maxCitation} color="#2D4DE0" height={4} />
                      </div>
                      <span style={{ fontSize: 12, color: 'rgba(10,10,26,0.6)', fontWeight: 700, textAlign: 'right' }}>{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By platform — top 5 per LLM */}
              <div>
                <div style={{ ...label, marginBottom: 10 }}>Top 5 per Platform</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {Object.entries(PLATFORM_LABELS).map(([k, l]) => {
                    const list = citationsByPlatform[k] || [];
                    return (
                      <div key={k}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <img src={PLATFORM_LOGOS[k]} alt={l} width={16} height={16} />
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#0A0A1A' }}>{l}</span>
                          <span style={{ fontSize: 10, color: 'rgba(10,10,26,0.35)' }}>
                            {list.length === 0 ? 'no citations' : `${list.length} publications`}
                          </span>
                        </div>
                        {list.length === 0 ? (
                          <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.35)', paddingLeft: 24 }}>—</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingLeft: 24 }}>
                            {list.slice(0, 5).map((c, i) => (
                              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 30px', alignItems: 'center', gap: 8, fontSize: 11 }}>
                                <span style={{ color: 'rgba(10,10,26,0.7)' }}>{c.publication}</span>
                                <span style={{ color: 'rgba(10,10,26,0.5)', fontWeight: 700, textAlign: 'right' }}>{c.count}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── SEGMENTATION: ARCHETYPE + COUNTRY ───────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={card}>
            <div style={sectionTitle}><Layers size={16} color="#2D4DE0" /> By Archetype</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(10,10,26,0.08)' }}>
                  <th style={{ ...label, padding: '8px 10px', textAlign: 'left' }}>Archetype</th>
                  <th style={{ ...label, padding: '8px 10px', textAlign: 'right' }}>Count</th>
                  <th style={{ ...label, padding: '8px 10px', textAlign: 'right' }}>Avg Score</th>
                  <th style={{ ...label, padding: '8px 10px', textAlign: 'right' }}>Avg Rev-at-Risk</th>
                </tr>
              </thead>
              <tbody>
                {segmentation.by_archetype.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(10,10,26,0.04)' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 600, color: '#0A0A1A', textTransform: 'capitalize' }}>{(row.archetype || '').replace(/_/g, ' ')}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'rgba(10,10,26,0.7)', fontWeight: 700 }}>{row.count}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: scoreColor(row.avg_score), fontWeight: 700 }}>{row.avg_score}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'rgba(10,10,26,0.7)' }}>{fmtNum(row.avg_revenue_at_risk)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={card}>
            <div style={sectionTitle}><Globe size={16} color="#2D4DE0" /> By Country</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(10,10,26,0.08)' }}>
                  <th style={{ ...label, padding: '8px 10px', textAlign: 'left' }}>Country</th>
                  <th style={{ ...label, padding: '8px 10px', textAlign: 'right' }}>Count</th>
                  <th style={{ ...label, padding: '8px 10px', textAlign: 'right' }}>Avg Score</th>
                </tr>
              </thead>
              <tbody>
                {segmentation.by_country.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(10,10,26,0.04)' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 600, color: '#0A0A1A' }}>{row.country || '—'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'rgba(10,10,26,0.7)', fontWeight: 700 }}>{row.count}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: scoreColor(row.avg_score), fontWeight: 700 }}>{row.avg_score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── MODULE FREQUENCY ────────────────────────────────────────── */}
        <div style={card}>
          <div style={sectionTitle}><Layers size={16} color="#2D4DE0" /> Active Module Frequency</div>
          <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)', marginBottom: 12 }}>
            How often each revenue / amenity module is active in the portfolio.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {module_frequency.map((m, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 50px', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: 'rgba(10,10,26,0.7)', fontWeight: 600, textTransform: 'capitalize' }}>{(m.module || '').replace(/_/g, ' ')}</span>
                <Bar value={m.count} max={maxModule} color="#2D4DE0" height={6} />
                <span style={{ fontSize: 12, color: 'rgba(10,10,26,0.6)', fontWeight: 700, textAlign: 'right' }}>{m.count}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
